"use client"

import useEmblaCarousel from "embla-carousel-react"
import Image, { StaticImageData } from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
interface CarouselItem {
  title: string
  imageUrl?: StaticImageData
  videoUrl?: string
  name: string
  professionString: string
  avatar: StaticImageData
}

interface PerspectiveCarouselProps {}

import { Text, VideoElement } from "@/elements"

import clsx from "clsx"

import a24 from "@/public/marketing/perspective-carousel/A24.png"
import alteredStyle from "@/public/marketing/perspective-carousel/AlteredStates.png"
import CherryXDover from "@/public/marketing/perspective-carousel/CherryXDover.png"
import crFashion from "@/public/marketing/perspective-carousel/CRFashionBook.png"
import perfectMag from "@/public/marketing/perspective-carousel/PerfectMag.png"
import yaeji from "@/public/marketing/perspective-carousel/Yaeji.png"

import alanaOHerlihy from "@/public/marketing/perspective-carousel/creatives/ Alana O’Herlihy.jpg"
import chaseShewbridge from "@/public/marketing/perspective-carousel/creatives/ Chase Shewbridge.jpg"
import dylanChavles from "@/public/marketing/perspective-carousel/creatives/ Dylan Chavles.jpg"
import estherFoster from "@/public/marketing/perspective-carousel/creatives/ Esther Foster .png"
import maraWeinstein from "@/public/marketing/perspective-carousel/creatives/ Mara Weinstein .jpg"
import nikArthur from "@/public/marketing/perspective-carousel/creatives/ Nik Arthur.jpg"
import violetSmith from "@/public/marketing/perspective-carousel/creatives/ Violet Smith.jpg"
import claireGillen from "@/public/marketing/perspective-carousel/creatives/Clare Gillen.png"
import dantePasquinelli from "@/public/marketing/perspective-carousel/creatives/Dante Pasquinelli.png"
import nealFarmer from "@/public/marketing/perspective-carousel/creatives/Neal Farmer.jpg"
import onda from "@/public/marketing/perspective-carousel/creatives/Onda.jpg"
import stillz from "@/public/marketing/perspective-carousel/creatives/STILLZ.jpg"
import { useInView } from "motion/react"
import { DragCursorWrapper } from "./useDragCursor"

const items: CarouselItem[] = [
  {
    imageUrl: a24,
    title: "A24 - 'I Saw the TV Glow' Packaging Design",
    name: "Chase Shewbridge",
    avatar: chaseShewbridge,
    professionString: "Art Director, Graphic Designer",
  },

  {
    imageUrl: crFashion,
    title: "CR Fashion Book Cover I Kylie Jenner",
    name: "Alana O'Herlihy",
    avatar: alanaOHerlihy,
    professionString: "Creative Director, Photographer",
  },
  {
    imageUrl: yaeji,
    title: "Yaeji Tour Design",
    name: "Claire Gillen",
    avatar: claireGillen,
    professionString: "Creative Director, Director, Photographer",
  },
  {
    imageUrl: perfectMag,
    title: "Bella Hadid - Perfect Magazine Cover",
    name: "Mara Weinstein",
    avatar: maraWeinstein,
    professionString: "Producer",
  },
  {
    videoUrl: `${process.env.NEXT_PUBLIC_NOVA_STATIC_CLOUDFRONT_DOMAIN}/marketing-site/NoLieAdidas/NoLieAdidas.m3u8`,
    title: "No Lie - Adidas AE1",
    name: "Onda",
    avatar: onda,
    professionString: "Creative Director, Director",
  },
  {
    imageUrl: alteredStyle,
    title: "Altered States",
    name: "Dylan Chavles",
    avatar: dylanChavles,
    professionString: "Groomer, Hair Stylist",
  },
  {
    imageUrl: CherryXDover,
    title: "CHERRY x DOVERSTREET MARKET",
    name: "Esther Foster",
    avatar: estherFoster,
    professionString: "Groomer, Makeup Artist",
  },
  {
    videoUrl: `${process.env.NEXT_PUBLIC_NOVA_STATIC_CLOUDFRONT_DOMAIN}/marketing-site/DuaLipaTraining/DuaLipaTraining.m3u8`,
    title: "Dua Lipa - Training Season",
    name: "Dante Pasquinelli",
    avatar: dantePasquinelli,
    professionString: "Colorist",
  },
  {
    videoUrl: `${process.env.NEXT_PUBLIC_NOVA_STATIC_CLOUDFRONT_DOMAIN}/marketing-site/BadBunnyMonaco/BadBunnyMonaco.m3u8`,
    title: "Bad Bunny - Monaco",
    name: "STILLZ",
    avatar: stillz,
    professionString: "Director",
  },
  {
    videoUrl: `${process.env.NEXT_PUBLIC_NOVA_STATIC_CLOUDFRONT_DOMAIN}/marketing-site/StayCoolVice/StayCoolVice.m3u8`,
    title: "Stay Cool with Vice",
    name: "Nik Arthur",
    avatar: nikArthur,
    professionString: "Animator, Director",
  },
  {
    videoUrl: `${process.env.NEXT_PUBLIC_NOVA_STATIC_CLOUDFRONT_DOMAIN}/marketing-site/KIMSHUIFW24/KIMSHUIFW24.m3u8`,
    title: "Kim Shui FW24",
    name: "Violet Smith",
    avatar: violetSmith,
    professionString: "Cinematographer",
  },
  {
    videoUrl: `${process.env.NEXT_PUBLIC_NOVA_STATIC_CLOUDFRONT_DOMAIN}/marketing-site/Kendrick_Lamar_N95_Short/Kendrick_Lamar_N95_Short.m3u8`,
    title: "Kendrick Lamar - N95",
    name: "Neal Farmer",
    avatar: nealFarmer,
    professionString: "Director, Editor",
  },
]

export const PerspectiveCarousel: React.FC<PerspectiveCarouselProps> = props => {
  const [isMobile, setIsMobile] = useState(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    startIndex: 4,
    loop: true,
    dragFree: false,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }

    const onScroll = () => {
      const progress = emblaApi.scrollProgress()
      /* 
      * math explanation
      emblaApi.scrollSnapList().length is items in carousel. e.g. 4 items = 4
      progess = % complete as decimal e.g. 4 items in carousel. index 0 = 0.0 | index 1 = 0.25 | index 2 = 0.5
      snapIndex = closest index to progress with respect to carousel length. e.g. 4 items. progress 0.25 = index 1
      
      however, this doesn't work for the last item in infinite scroll, so we need to check if progress is greater than 1 - 1/carousel length / 2
      continuing from the above example, if progress is greater than 0.87.5, we need to set the selected index to 0
      this is because it is half was between index 3 and 0 (the last and first item)
      */
      const snapIndex = Math.round(progress * emblaApi.scrollSnapList().length)
      if (progress > 1 - 1 / emblaApi.scrollSnapList().length / 2) {
        setSelectedIndex(0)
      } else {
        setSelectedIndex(snapIndex)
      }
    }

    emblaApi.on("select", onSelect)
    emblaApi.on("scroll", onScroll)
    onSelect() // Initial selection

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("scroll", onScroll)
    }
  }, [emblaApi])

  const containerRef = useRef<HTMLDivElement>(null)
  const isInvView = useInView(containerRef)

  // Add autoplay effect
  useEffect(() => {
    if (!emblaApi) return

    let autoplayInterval = setInterval(() => {
      isInvView ? emblaApi.scrollNext() : null
    }, 5000) // Scrolls every 3 seconds

    // Stop autoplay on user interaction
    const onPointerDown = () => {
      clearInterval(autoplayInterval)
    }

    emblaApi.on("pointerDown", onPointerDown)
    emblaApi.containerNode().addEventListener("mouseenter", () => {
      clearInterval(autoplayInterval)
    })
    emblaApi.containerNode().addEventListener("mouseleave", () => {
      autoplayInterval = setInterval(() => {
        emblaApi.scrollNext()
      }, 5000)
    })

    return () => {
      clearInterval(autoplayInterval)
      emblaApi.off("pointerDown", onPointerDown)
    }
  }, [emblaApi])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 900)
      }

      handleResize() // Initial check
      window.addEventListener("resize", handleResize)

      return () => {
        window.removeEventListener("resize", handleResize)
      }
    }
  }, [setIsMobile])

  const getItemStyle = (index: number) => {
    if (isMobile) return {}
    if (index === selectedIndex)
      return {
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }

    return {
      transform:
        Math.abs(index - selectedIndex) <= 1
          ? "perspective(1000px) rotateX(8deg) rotateY(45deg) rotate(6deg) scale(0.85) translateX(-0px)"
          : "perspective(1000px) rotateX(-3deg) rotateY(-33deg) rotate(-2deg) scale(0.55) translateX(0px)",
      transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      opacity: "0.7",
      filter: "brightness(0.8)",
    }
  }

  return (
    <div className="relative mt-[-90px] flex w-screen flex-grow-0 flex-col md:mt-[-180px]" ref={containerRef}>
      <div className="relative w-full select-none sm:px-12">
        <DragCursorWrapper>
          <div className="w-full" ref={emblaRef}>
            <div className="flex h-[500px] items-center gap-x-4 px-5 sm:mx-auto sm:px-0 md:gap-x-16">
              {items.map((item, index) => (
                <div
                  onClick={() => emblaApi?.scrollTo(index)}
                  key={index}
                  className="h-auto w-[300px] min-w-0 flex-none">
                  <div className="relative flex h-auto w-[300px] shrink-0 rounded-md">
                    {item.imageUrl ? (
                      <Image
                        src={item.imageUrl}
                        style={getItemStyle(index)}
                        width={300}
                        height={300}
                        alt={item.title}
                        loading="lazy"
                        className={clsx(
                          "h-auto w-[300px] shrink-0 object-contain transition-all",
                          selectedIndex === index ? "rounded-t-xl" : "rounded-t-xl md:rounded-xl"
                        )}
                      />
                    ) : null}
                    {item.videoUrl ? (
                      <VideoElement
                        src={item.videoUrl}
                        autoPlay
                        muted
                        style={getItemStyle(index)}
                        loop
                        playsInline
                        preload="none"
                        controls={false}
                        controlsList="nodownload"
                        className={clsx(
                          "h-auto w-[300px] shrink-0 object-contain transition-all",
                          selectedIndex === index ? "rounded-t-xl" : "rounded-t-xl md:rounded-xl"
                        )}
                      />
                    ) : null}
                  </div>
                  <div
                    className={clsx(
                      "flex flex-col gap-y-2 rounded-b-xl bg-marketing-background-2-light p-5 transition-all duration-200 dark:bg-marketing-background-2-dark",
                      index === selectedIndex ? "opacity-100 delay-300" : "delay-0 md:opacity-0"
                    )}>
                    <Text className="text-marketing-primary-light dark:text-marketing-primary-dark">{item.title}</Text>
                    <div className="mt-auto flex items-center gap-x-2">
                      <div className="relative size-7">
                        <Image
                          src={item.avatar}
                          alt={item.name}
                          width={180}
                          height={180}
                          className="h-full w-full rounded-full"
                        />
                      </div>
                      <div className="flex flex-col">
                        <Text className="text-marketing-primary-light dark:text-marketing-primary-dark" variant="body2">
                          {item.name}
                        </Text>
                        <Text className="text-marketing-secondary" variant="caption">
                          {item.professionString}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DragCursorWrapper>
      </div>
    </div>
  )
}
